import { useQuery } from "@tanstack/react-query";
import { Select, Tag } from "antd";
import { useDeferredValue, useState } from "react";
import { api, unwrap, type Schemas } from "@/api/client";
import { useI18n } from "@/i18n";

export type PurchaseOrder = Schemas["PurchaseOrder"];
export type PurchaseOrderRow = Schemas["PurchaseOrderRow"];
export type PurchaseOrderStatus = Schemas["PurchaseOrderStatus"];
export type Supplier = Schemas["Supplier"];

export const PO_STATUSES: PurchaseOrderStatus[] = ["draft", "submitted", "approved", "sent", "partial", "received", "closed", "cancelled"];

const statusColor: Record<PurchaseOrderStatus, string> = {
  draft: "default",
  submitted: "gold",
  approved: "blue",
  sent: "processing",
  partial: "cyan",
  received: "success",
  closed: "default",
  cancelled: "error",
};

export function PoStatusTag({ status }: { status: PurchaseOrderStatus }) {
  const { t } = useI18n();
  return <Tag color={statusColor[status]}>{t(`po.status.${status}`)}</Tag>;
}

/** PPN in basis points as a percentage: 1100 → "11". */
export function taxPercent(bp: number) {
  return String(bp / 100).replace(".", ",");
}

/** Active suppliers, searchable (purchase order form, list filter). */
export function SupplierSelect({ value, onChange, allowClear, placeholder, style }: {
  value?: string;
  onChange?: (id: string | undefined, supplier?: Supplier) => void;
  allowClear?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
}) {
  const { t } = useI18n();
  const [search, setSearch] = useState("");
  const q = useDeferredValue(search.trim());
  const list = useQuery({
    queryKey: ["suppliers", "pick", q],
    queryFn: () => unwrap(api.GET("/v1/suppliers", { params: { query: { status: "active", limit: 30, ...(q ? { q } : {}) } } })),
  });
  // Keep the chosen one in the options while searching for others.
  const chosen = useQuery({
    queryKey: ["suppliers", "detail", value],
    queryFn: () => unwrap(api.GET("/v1/suppliers/{supplierID}", { params: { path: { supplierID: value! } } })),
    enabled: !!value && !(list.data?.items ?? []).some((s) => s.id === value),
  });
  const items = [...(list.data?.items ?? [])];
  if (chosen.data && !items.some((s) => s.id === chosen.data.id)) items.unshift(chosen.data);
  return (
    <Select<string>
      showSearch={{ filterOption: false, onSearch: setSearch }}
      allowClear={allowClear}
      value={value}
      onChange={(id) => onChange?.(id, items.find((s) => s.id === id))}
      loading={list.isFetching}
      placeholder={placeholder ?? t("po.supplier")}
      style={{ width: "100%", ...style }}
      notFoundContent={t("po.noSuppliers")}
      options={items.map((s) => ({
        value: s.id,
        label: (
          <span>
            <span className="z-num">{s.code}</span> · {s.name}
          </span>
        ),
      }))}
    />
  );
}

/** Rupiah in an InputNumber: 38.500 while typing, a plain number out. */
export const rupiahInput = {
  formatter: (v: number | string | undefined) => (v === undefined || v === "" ? "" : String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ".")),
  parser: (v: string | undefined) => Number((v ?? "").replace(/[^\d]/g, "")),
};

/** The same, for an amount that may be negative (a discount). */
export const rupiahSignedInput = {
  formatter: rupiahInput.formatter,
  parser: (v: string | undefined) => {
    const n = rupiahInput.parser(v);
    return (v ?? "").trim().startsWith("-") ? -n : n;
  },
};
